import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/useAuth';
import { SupportChat } from '../components/SupportChat';
import { GoLiveDialog } from './GoLiveDialog';
import {
  ConnectionContext,
  type ConnectionInfo,
} from './links';

/**
 * Dashboard chrome: a left sidebar nav + content area.
 *
 * Replaces the bare AppShell-wrapped DashboardPlaceholder. The nav lists all
 * six M10 views; the ones not yet built (M10b/M10c) render a "Coming soon"
 * placeholder so the nav structure is stable across the milestone split.
 *
 * The nav stays inside the AppShell (top bar) — that outer chrome is shared
 * with the onboarding wizard. This layout adds the dashboard-specific sidebar.
 *
 * On mount, fetches the tenant connection (portalId + stripeAccountId) once
 * via GET /api/admin/overview and provides it through ConnectionContext, so
 * every view can build "View in Stripe" / "View in HubSpot" deep links without
 * each re-fetching the connection. The fetch is best-effort — if it fails the
 * context just holds nulls (links are omitted, the dashboard still renders).
 *
 * Reference: docs/README.md §M10-rescoped (dashboard structure)
 */
interface NavItem {
  to: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Overview' },
  { to: '/dashboard/subscriptions', label: 'Subscriptions' },
  { to: '/dashboard/customers', label: 'Customers' },
  { to: '/dashboard/products', label: 'Products' },
  { to: '/dashboard/charges', label: 'Charges' },
  { to: '/dashboard/invoices', label: 'Invoices' },
  { to: '/dashboard/disputes', label: 'Disputes' },
  { to: '/dashboard/refunds', label: 'Refunds' },
  { to: '/dashboard/subscription-changes', label: 'Sub. Changes' },
  { to: '/dashboard/audit', label: 'Audit log' },
  { to: '/dashboard/settings', label: 'Settings' },
  { to: '/dashboard/team', label: 'Team' },
  { to: '/dashboard/billing', label: 'Billing' },
];

export function DashboardLayout() {
  // Collapsible sidebar (QA #4) — the fixed w-56 nav ate too much horizontal
  // space, clamping the dashboard tables. The toggle persists in localStorage
  // so the user's choice survives navigation. Collapsed = narrow rail (icons
  // only); Expanded = full labels. Defaults to expanded.
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('dashNavCollapsed') === '1';
  });
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('dashNavCollapsed', next ? '1' : '0');
      } catch {
        /* localStorage may be unavailable (private mode) — non-fatal */
      }
      return next;
    });
  };

  // QA-F #5: lock the dashboard while the historical import is running.
  // Direct URL access to /dashboard/* mid-import must bounce to /onboarding
  // (the progress screen) — no half-populated dashboard. Probed once on mount;
  // any fetch error or non-running state allows the dashboard (best-effort,
  // never blocks the user on a transient failure). A completed/failed import
  // unlocks the dashboard immediately.
  const [locked, setLocked] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get<{
        missing: boolean;
        status: string;
        progress?: { phase?: string; } | null;
      }>('onboarding/bootstrap/workflow-status')
      .then((wf) => {
        if (cancelled) return;
        const completed =
          wf.status === 'complete' || wf.progress?.phase === 'done';
        const running =
          !wf.missing &&
          !completed &&
          wf.status !== 'errored' &&
          wf.status !== 'terminated';
        setLocked(running);
      })
      .catch(() => {
        if (cancelled) return;
        setLocked(false); // best-effort — allow the dashboard
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the tenant connection once (portalId + stripeAccountId) so every
  // nested view can build deep links. Best-effort: on error the context stays
  // at nulls (links are simply omitted). Re-fetches never happen — the
  // connection is stable for the session.
  const [connection, setConnection] = useState<ConnectionInfo>({
    portalId: null,
    stripeAccountId: null,
  });
  useEffect(() => {
    let cancelled = false;
    api
      .get<{
        connection: { portalId: string | null; stripeAccountId: string | null; testMode?: boolean; };
      }>('admin/overview')
      .then((data) => {
        if (cancelled) return;
        setConnection({
          portalId: data.connection.portalId,
          stripeAccountId: data.connection.stripeAccountId,
          testMode: data.connection.testMode ?? false,
        });
      })
      .catch(() => {
        /* best-effort — context stays null; links omitted */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // M16 T2: viewer bounce. The dashboard is admin/operator only; a viewer
  // (logged in but with no role row) is bounced to a "no access" screen. The
  // role comes from /api/auth/me (read from the tenant DO). While loading we
  // render nothing (avoid a flash of content a viewer shouldn't see).
  const { principal, loading: authLoading } = useAuth();
  const canViewDashboard = principal?.capabilities.viewDashboard === true;
  const isAdmin = principal?.role === 'admin';
  // Go-live dialog (test-mode banner action, admin only).
  const [goLiveOpen, setGoLiveOpen] = useState(false);

  // Review r6: distinguish "no session" from "viewer role". A null principal
  // once loading settles means /api/auth/me returned 401 (missing/expired
  // session cookie) or the fetch failed — NOT a role problem. Bounce to
  // /login for one-click HubSpot re-auth (same convention as RootRedirect).
  // The old fail-closed behavior rendered the "ask an admin for a role" dead
  // end here, which is what the round-6 reviewer hit after authorization.
  if (authLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-sm text-ink-400">
        Loading…
      </div>
    );
  }
  if (!principal) {
    return <Navigate to="/login" replace />;
  }
  if (principal.roleLookupFailed) {
    // Review r6: the role lookup ERRORED (e.g. the tenant's Stripe
    // credentials are revoked and the DO couldn't answer). The fail-closed
    // viewer role is a placeholder, not a real denial — offer a retry
    // instead of the misleading "ask an admin" dead end.
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h1 className="text-lg font-semibold text-ink-800">
          Couldn&rsquo;t verify your access
        </h1>
        <p className="mt-2 text-sm text-ink-500">
          We couldn&rsquo;t reach the workspace to check your role. This is
          usually temporary — try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn-primary mt-4 inline-flex items-center justify-center"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!canViewDashboard) {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h1 className="text-lg font-semibold text-ink-800">No access</h1>
        <p className="mt-2 text-sm text-ink-500">
          You don&rsquo;t have permission to view this dashboard. Ask an admin
          to grant you the Operator or Admin role.
        </p>
        <a
          href="/auth/logout"
          className="mt-4 inline-block text-xs font-medium text-ink-400 transition-colors hover:text-warn-600"
        >
          Log out
        </a>
      </div>
    );
  }

  return (
    <ConnectionContext.Provider value={connection}>
      {/* QA-F #5: while the import is running, bounce every dashboard route
          to the onboarding progress screen. */}
      {locked ? <Navigate to="/onboarding" replace /> : null}
      {/* M31 test channel: banner the data provenance so test mode is never
          invisible after the wizard. Admins get the go-live action (reset
          synced test data + disconnect → re-enter the wizard for a live
          connection). */}
      {connection.testMode && (
        <div
          role="status"
          className="mx-auto mb-4 flex max-w-5xl flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800"
        >
          <span className="min-w-0 flex-1">
            <strong>Test mode.</strong> This workspace is connected to a Stripe
            account in test mode — the subscriptions, invoices, and customers
            below come from Stripe test data, and the records written to this
            HubSpot portal are test records.
          </span>
          {isAdmin && (
            <button
              type="button"
              className="btn btn-secondary shrink-0"
              onClick={() => setGoLiveOpen(true)}
            >
              Go live
            </button>
          )}
        </div>
      )}
      {goLiveOpen && <GoLiveDialog onClose={() => setGoLiveOpen(false)} />}
      <div className="flex gap-4">
        {/* Sidebar nav — sticky so it stays visible while content scrolls.
            Collapsible (QA #4): collapsed shows a narrow rail + the first
            letter of each label; expanded shows full labels. */}
        <aside
          className={[
            'hidden shrink-0 md:block',
            collapsed ? 'w-10' : 'w-52',
          ].join(' ')}
        >
          <nav className="sticky top-20 space-y-1">
            {/* Collapse toggle — always visible at the top of the rail. */}
            <button
              type="button"
              onClick={toggleCollapsed}
              className="mb-2 flex w-full items-center justify-center rounded-lg px-2 py-1.5 text-xs font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-800"
              title={collapsed ? 'Expand menu' : 'Collapse menu'}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
              {collapsed ? '›' : '‹ Collapse'}
            </button>
            {/* M16 T2: hide admin-only nav items (Settings, Team) from
                operators. Routes themselves 403 non-admins server-side.
                M21: Billing is financial management — same admin-only set. */}
            {NAV_ITEMS.filter((item) =>
              item.to === '/dashboard/settings' ||
                item.to === '/dashboard/team' ||
                item.to === '/dashboard/billing'
                ? isAdmin
                : true
            ).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/dashboard'}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  [
                    'flex items-center rounded-lg px-2 py-2 text-sm font-medium transition-colors',
                    collapsed ? 'justify-center' : 'justify-between px-3',
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-ink-600 hover:bg-ink-100 hover:text-ink-800',
                  ].join(' ')
                }
              >
                <span>
                  {collapsed ? (
                    // Rail mode: first letter only, keeps the nav scannable.
                    <span className="font-semibold">{item.label.charAt(0)}</span>
                  ) : (
                    item.label
                  )}
                </span>
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Content area — each view owns its own max-width + spacing.
            Expands to fill the space freed by the collapsed sidebar (QA #4). */}
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>

      {/* M29 T5: docs-grounded AI support widget — dashboard-only by
          construction (mounted here, never in the wizard/login screens). */}
      <SupportChat />
    </ConnectionContext.Provider>
  );
}

/** Small section header used by views. */
export function ViewHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold text-ink-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
